import React, { useState, useEffect } from 'react';
import { LinkedInAuthService, LinkedInAuthStatus } from '../../services/linkedinAuthService';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

interface LinkedInAuthManagerProps {
  showTitle?: boolean;
  compact?: boolean;
  onStatusChange?: (isConnected: boolean) => void;
  initialAuthStatus?: LinkedInAuthStatus;
}

const LinkedInAuthManager: React.FC<LinkedInAuthManagerProps> = ({
  showTitle = true,
  compact = false,
  onStatusChange,
  initialAuthStatus
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [authStatus, setAuthStatus] = useState<LinkedInAuthStatus>({ isConnected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (initialAuthStatus) {
      // Use pre-fetched data if provided
      setAuthStatus(initialAuthStatus);
      setLoading(false);
      if (onStatusChange) {
        onStatusChange(initialAuthStatus.isConnected);
      }
    } else if (user) {
      // Otherwise fetch data
      loadLinkedInStatus();
    }
  }, [user, initialAuthStatus]);

  const loadLinkedInStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const status = await LinkedInAuthService.getLinkedInAuthStatus();
      setAuthStatus(status);

      if (onStatusChange) {
        onStatusChange(status.isConnected);
      }
    } catch (error) {
      console.error('Error loading LinkedIn status:', error);
      setError('Failed to load LinkedIn connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);

      const result = await LinkedInAuthService.connectLinkedIn();

      if (!result.success) {
        throw result.error || new Error('Failed to connect LinkedIn');
      }

      // The OAuth flow will redirect the user, so we don't need to update state here
      // The status will be updated when the user returns from the OAuth flow
    } catch (error: any) {
      console.error('Error connecting LinkedIn:', error);
      setError(error.message || 'Failed to connect LinkedIn account');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setDisconnecting(true);
      setError(null);
      setSuccess(null);

      const result = await LinkedInAuthService.disconnectLinkedIn();

      if (!result.success) {
        throw result.error || new Error('Failed to disconnect LinkedIn');
      }

      setSuccess(t('linkedinAuth.disconnectedSuccess'));
      await loadLinkedInStatus(); // Refresh status
    } catch (error: any) {
      console.error('Error disconnecting LinkedIn:', error);
      setError(error.message || 'Failed to disconnect LinkedIn account');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleRefresh = async () => {
    await loadLinkedInStatus();
  };

  if (loading) {
    return (
      <div className={`linkedin-auth-manager ${compact ? 'compact' : ''}`}>
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">{t('linkedinAuth.loading')}</span>
          </div>
          <span className="text-muted">{t('linkedinAuth.checkingConnection')}</span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="linkedin-auth-manager compact">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <i className="bi bi-linkedin text-primary me-2"></i>
            <span className={authStatus.isConnected ? 'text-success' : 'text-muted'}>
              LinkedIn {authStatus.isConnected ? t('linkedinAuth.connected') : t('linkedinAuth.notConnected')}
            </span>
          </div>

          {authStatus.isConnected ? (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
            >
              {disconnecting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  {t('linkedinAuth.disconnecting')}
                </>
              ) : (
                t('linkedinAuth.disconnect')
              )}
            </button>
          ) : (
            <button
              className="btn btn-primary btn-sm"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                  {t('linkedinAuth.connecting')}
                </>
              ) : (
                t('linkedinAuth.connect')
              )}
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger alert-sm mt-2 mb-0">
            <small>{error}</small>
          </div>
        )}

        {success && (
          <div className="alert alert-success alert-sm mt-2 mb-0">
            <small>{success}</small>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="linkedin-auth-manager">
      {showTitle && (
        <div className="mb-3">
          <h5 className="mb-1">
            <i className="bi bi-linkedin text-primary me-2"></i>
            {t('linkedinAuth.linkedinConnection')}
          </h5>
          <p className="text-muted mb-0">
            {t('linkedinAuth.manageDescription')}
          </p>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col">
              <div className="d-flex align-items-center mb-2">
                <div className={`status-indicator me-3 ${authStatus.isConnected ? 'connected' : 'disconnected'}`}>
                  <i className={`bi ${authStatus.isConnected ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'} fs-4`}></i>
                </div>
                <div>
                  <h6 className="mb-0">
                    {authStatus.isConnected ? t('linkedinAuth.linkedinConnected') : t('linkedinAuth.linkedinNotConnected')}
                  </h6>
                  {authStatus.isConnected && authStatus.name && (
                    <small className="text-muted">{t('linkedinAuth.connectedAs', { name: authStatus.name })}</small>
                  )}
                  {authStatus.isConnected && authStatus.connectedAt && (
                    <small className="text-muted d-block">
                      {t('linkedinAuth.connectedOn', { date: new Date(authStatus.connectedAt).toLocaleDateString() })}
                    </small>
                  )}
                </div>
              </div>

              {authStatus.isConnected && (
                <div className="linkedin-benefits">
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    {t('linkedinAuth.canCreateProjects')}
                  </small>
                </div>
              )}

              {!authStatus.isConnected && (
                <div className="linkedin-info">
                  <small className="text-muted">
                    {t('linkedinAuth.connectToVerify')}
                  </small>
                </div>
              )}
            </div>

            <div className="col-auto">
              <div className="d-flex gap-2">
                {authStatus.isConnected ? (
                  <>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      {t('linkedinAuth.refresh')}
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                    >
                      {disconnecting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          {t('linkedinAuth.disconnecting')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-unlink me-1"></i>
                          {t('linkedinAuth.disconnect')}
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleConnect}
                    disabled={connecting}
                  >
                    {connecting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        {t('linkedinAuth.connecting')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-linkedin me-1"></i>
                        {t('linkedinAuth.connectLinkedin')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mt-3 mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success mt-3 mb-0">
              <i className="bi bi-check-circle me-2"></i>
              {success}
            </div>
          )}
        </div>
      </div>

      {!authStatus.isConnected && (
        <div className="mt-3">
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title">
                <i className="bi bi-info-circle text-info me-2"></i>
                {t('linkedinAuth.whyConnect')}
              </h6>
              <ul className="list-unstyled mb-0">
                <li className="mb-1">
                  <i className="bi bi-check text-success me-2"></i>
                  {t('linkedinAuth.verifyIdentity')}
                </li>
                <li className="mb-1">
                  <i className="bi bi-check text-success me-2"></i>
                  {t('linkedinAuth.buildTrust')}
                </li>
                <li className="mb-1">
                  <i className="bi bi-check text-success me-2"></i>
                  {t('linkedinAuth.accessFeatures')}
                </li>
                <li className="mb-0">
                  <i className="bi bi-check text-success me-2"></i>
                  {t('linkedinAuth.meetRequirements')}
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedInAuthManager;